import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchNucliaSessionId, clearSessionId } from '../services/nuclia-session-service';

interface User {
  email: string | null;
  username: string | null;
  roles: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  nucliaSessionId: string | null;
  hasRole: (role: string) => boolean;
  login: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  register: (email: string, password: string, confirmPassword: string, role: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [nucliaSessionId, setNucliaSessionId] = useState<string | null>(null);

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/auth/user', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUser({
            email: data.email,
            username: data.username,
            roles: data.roles || []
          });
          const sessionId = await fetchNucliaSessionId();
          setNucliaSessionId(sessionId);
        } else {
          setUser(null);
          setNucliaSessionId(null);
        }
      } else {
        setUser(null);
        setNucliaSessionId(null);
      }
    } catch (error) {
      console.error('Error checking authentication:', error);
      setUser(null);
      setNucliaSessionId(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string, rememberMe: boolean) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email, password, rememberMe }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    const data = await response.json();
    setUser({
      email: data.email,
      username: data.username,
      roles: data.roles || []
    });
    
    const sessionId = await fetchNucliaSessionId();
    setNucliaSessionId(sessionId);
  };

  const register = async (email: string, password: string, confirmPassword: string, role: string) => {
    const response = await fetch('/api/auth/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email, password, confirmPassword, role }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Registration failed');
    }

    alert('Registration successful!');

    // Redirect
    window.location.href = '/';
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setNucliaSessionId(null);
      clearSessionId();
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) ?? false;
  };

  const isAdmin = hasRole('Admin');

  const value: AuthContextType = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    isAdmin,
    nucliaSessionId,
    hasRole,
    login,
    register,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
